import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, RED_LIGHT, GREEN, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";

// Tier styling. Unseen is deliberately quiet rather than alarming: an
// unpractised technique is an invitation, not a gap in a checklist.
const TIER_STYLE = {
  // Tinted background, so the pill needs a slightly darker foreground than
  // MUTED-on-white to land in the same place.
  unseen: { label: "Not yet", fg: "#5f5f5f", bg: "#f2f2f2" },
  Tried: { label: "Tried", fg: "#7a6a2f", bg: "#fdf6e0" },
  Practiced: { label: "Practiced", fg: RED, bg: RED_LIGHT },
  Comfortable: { label: "Comfortable", fg: GREEN, bg: GREEN_LIGHT },
  Instinct: { label: "Instinct", fg: "#fff", bg: GREEN },
};

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "14px",
  border: "1.5px solid",
  borderColor: BORDER,
  overflow: "hidden",
  mb: 2,
};

const cardLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: MUTED,
  fontFamily: "Roboto, sans-serif",
};

function TierPill({ tier }) {
  const style = TIER_STYLE[tier] || TIER_STYLE.unseen;
  return (
    <Box
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        color: style.fg,
        bgcolor: style.bg,
        borderRadius: "999px",
        px: "9px",
        py: "3px",
        whiteSpace: "nowrap",
        fontFamily: "Roboto, sans-serif",
      }}
    >
      {style.label}
    </Box>
  );
}

TierPill.propTypes = { tier: PropTypes.string };

function TechniqueRow({ technique, expanded, onToggle, onTeachingRecipeClick }) {
  const isUnseen = technique.tier === "unseen";
  const remaining =
    technique.next_threshold == null ? null : technique.next_threshold - technique.reps;

  return (
    <Box
      sx={{
        px: 3,
        py: "13px",
        borderBottom: "1px solid #f5f5f5",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          border: "none",
          bgcolor: "transparent",
          p: 0,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            color: isUnseen ? MUTED : "#222",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {technique.display_name}
        </Box>
        <TierPill tier={technique.tier} />
      </Box>

      {technique.blurb && (
        <Box
          sx={{
            fontSize: 12.5,
            color: MUTED,
            mt: "5px",
            lineHeight: 1.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {technique.blurb}
        </Box>
      )}

      {!isUnseen && (
        <Box
          sx={{
            fontSize: 11.5,
            color: MUTED,
            mt: "6px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {technique.reps} {technique.reps === 1 ? "time" : "times"}
          {remaining != null && remaining > 0
            ? ` · ${remaining} more to level up`
            : " · you've gone as far as this one goes"}
        </Box>
      )}

      {/* The empty state is the point of this screen: it is the only place in
          the product that says "here is something you can't do yet, and here
          is how to fix that". */}
      {isUnseen && technique.teaching_recipes?.length > 0 && (
        <Box sx={{ mt: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {technique.teaching_recipes.map((recipe) => (
            <Box
              key={recipe.id}
              component="button"
              onClick={() => onTeachingRecipeClick(recipe)}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: RED,
                bgcolor: RED_LIGHT,
                border: "none",
                borderRadius: "6px",
                px: "9px",
                py: "5px",
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {recipe.title}
            </Box>
          ))}
        </Box>
      )}

      {expanded && technique.why_it_works && (
        <Box
          sx={{
            fontSize: 12.5,
            color: "#444",
            mt: "9px",
            lineHeight: 1.55,
            bgcolor: "#fafafa",
            borderRadius: "8px",
            p: "10px 12px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {technique.why_it_works}
        </Box>
      )}
    </Box>
  );
}

TechniqueRow.propTypes = {
  technique: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onTeachingRecipeClick: PropTypes.func.isRequired,
};

/**
 * The full skill grid — every technique the vocabulary displays, by family.
 *
 * Deferred techniques never arrive here: /api/techniques/me filters them out on
 * `position IS NOT NULL`, because they carry no blurb and no why_it_works and
 * would render as a blank row. This component must not try to compensate for a
 * missing blurb by inventing one.
 *
 * Lives on the Your Kitchen page, which is the only place it is rendered. It
 * used to sit on /profile below the password field with no nav entry, where
 * testers called it the best thing in the product and found it by accident.
 */
function TechniqueGrid() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.techniques.me)
      .then((res) => setProfile(res.data))
      .catch(() => setFailed(true));
  }, []);

  // Fail quiet: the rest of the profile page is more important than this card,
  // and a skill grid that couldn't load is not worth an error banner.
  if (failed || !profile) return null;

  const families = profile.families || [];
  if (families.length === 0) return null;

  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
        {/* Labelled "Techniques" rather than "Your Kitchen": that is now the
            page title directly above it, and a card repeating its own page
            heading reads as a rendering bug. */}
        {/* No prose summary. `profile.summary` ("You've made a start on…
            Next up…") said in a sentence what the tier badges two hundred
            pixels below say at a glance, and a skim-reading tester read the
            sentence instead of the grid. The field is still served — nothing
            here is the right place to decide it should stop being. */}
        <Box sx={cardLabelSx}>Techniques</Box>
      </Box>

      {families.map((family) => (
        <Box key={family.family}>
          <Box
            sx={{
              px: 3,
              py: "9px",
              bgcolor: "#fbfbfb",
              borderBottom: "1px solid #f5f5f5",
              ...cardLabelSx,
            }}
          >
            {family.label}
          </Box>
          {family.techniques.map((technique) => (
            <TechniqueRow
              key={technique.id}
              technique={technique}
              expanded={expandedId === technique.id}
              onToggle={() =>
                setExpandedId((prev) => (prev === technique.id ? null : technique.id))
              }
              onTeachingRecipeClick={(recipe) =>
                // Same shape the cookbook's own rows hand to openRecipe(); the
                // detail view fills the rest in from /details.
                navigate("/cookbook", {
                  state: {
                    view: "recipe",
                    selectedRecipe: { recipe_id: recipe.id, title: recipe.title },
                  },
                })
              }
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default TechniqueGrid;
